
import { useState } from "react";
import { AddressDetails, SavedAddress } from "@/types/address";
import { GooglePlacesSelectValue } from "@/types/google-places";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface AddressSelectionHookProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPlaceSelect: (place: GooglePlacesSelectValue) => void;
  onClear?: () => void;
}

export const useAddressSelection = ({ onChange, onPlaceSelect, onClear }: AddressSelectionHookProps) => {
  const [showFields, setShowFields] = useState(false);

  const fetchAddressDetails = async (addressId: string): Promise<SavedAddress | null> => {
    const { data, error } = await supabase
      .from('address_book')
      .select('*')
      .eq('id', addressId)
      .single();

    if (error) {
      console.error('Error fetching address details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch address details",
        variant: "destructive",
      });
      return null;
    }

    console.log('Fetched address details:', data);
    return data;
  };

  const handleAddressSelect = async (addressId: string) => {
    const selectedAddress = await fetchAddressDetails(addressId);
    if (!selectedAddress) return;

    // Create a synthetic place object to maintain consistency with Google Places
    const syntheticPlace: GooglePlacesSelectValue = {
      value: {
        description: selectedAddress.street,
        place_id: selectedAddress.id,
      },
      label: selectedAddress.label || selectedAddress.street,
    };

    // Call onPlaceSelect to maintain state consistency
    onPlaceSelect(syntheticPlace);

    // Directly update form fields with database values
    const updateField = (name: string, value: string) => {
      onChange({
        target: { name, value }
      } as React.ChangeEvent<HTMLInputElement>);
    };

    // Map database fields to form fields
    updateField('street', selectedAddress.street);
    updateField('street2', selectedAddress.street2 || '');
    updateField('city', selectedAddress.city);
    updateField('province', selectedAddress.province);
    updateField('postalCode', selectedAddress.postal_code);
    updateField('country', selectedAddress.country);
    
    setShowFields(true);
  };

  const handleClear = () => {
    setShowFields(false);
    // Call the onClear prop to clear form data in parent component
    if (onClear) {
      onClear();
    }
  };

  return {
    showFields,
    setShowFields,
    handleAddressSelect,
    handleClear,
  };
};
